import AbstractComponent from "./abstract-component";
import {utils, config} from "@/tools/pdf";

const {zero} = utils;

/**
 * @param {Object[]} data Data objects.
 *  []: Object
 *    title
 *    value (Pre-formatted string)
 *
 * @param {Object} options
 *  type: "row" row|grid
 *  icon: null
 *  iconPosition: "inline" inline|top
 *  title: ""
 *  subTitle: "" (optional)
 *  description: "" (optional)
 *  sectionTitle: "" Adds a section title on the bottom of the card.
 */
class ContentCard extends AbstractComponent {
  getDefaultOptions() {
    return {
      type: 'row',
      icon: null,
      iconPosition: 'inline',
      title: '',
      subTitle: '',
      description: '',
      sectionTitle: '',
      iconWidth: 40,
      gridTitleStyle: 'cardGridTitle',
      gridValueStyle: 'cardGridValue',
    }
  }

  render() {
    return [
      this.renderSectionTitle(),
      {
        table: {
          widths: ['50%', '50%'],
          heights: this.getRowHeight,
          body: [
            this.renderTopIcon(),
            this.renderHeader(),
            this.renderDescription(),
            this.renderData(),
          ].filter(Boolean)
        },
        layout: this.getLayout(),
        margin: [20, 16, 20, 10]
      }
    ].filter(Boolean)
  }

  renderSectionTitle() {
    const {options: {sectionTitle}} = this;

    if (!sectionTitle) return false;

    return {
      table: {
        widths: ['100%'],
        body: [[
          {
            text: sectionTitle,
            style: 'cardSectionTitle'
          }
        ]]
      },
      margin: 0,
      style: 'noBorders',
      layout: {
        fillColor: () => '#fff',
        hLineWidth: zero,
        vLineWidth: zero,
        paddingLeft: zero,
        paddingRight: zero,
        paddingTop: zero,
        paddingBottom: zero,
      },
    };
  }

  renderTopIcon() {
    const {options: {icon, iconPosition, iconWidth}} = this;

    if ('top' !== iconPosition || !icon)
      return false;

    return [{
      ...icon,
      colSpan: 2,
      width: iconWidth,
    }]
  }

  renderHeader() {
    const {options: {idx, icon, iconPosition, title, subTitle, iconWidth}} = this;

    return [{
      colSpan: 2,
      columns: [
        icon && 'inline' === iconPosition && {...icon, width: iconWidth, margin: [ 0, -10, 0, 0 ]},
        {
          text: [
            // Index
            idx && {text: `${idx}. `, style: 'cardTitleIdx'},
            // Title
            title && {text: title, style: icon && 'inline' === iconPosition ? 'cardTitleBig' : 'cardTitle'},
            subTitle && {text: ' ' + subTitle, style: 'cardSubTitle'},
          ].filter(Boolean)
        }
      ].filter(Boolean),
      margin: [
        0,
        4,
        0,
        'inline' === iconPosition ? 0 : 4
      ]
    }]
  }

  renderDescription() {
    const {options: {description}} = this;

    return description && [
      {
        colSpan: 2,
        text: description,
        margin: [0, -20, 0, 4],
        style: 'cardDescription',
      }
    ]
  }

  renderData() {
    switch (this.options.type) {
      case 'grid':
        return this._renderAsGrid();
      case 'row':
        return this._renderAsRows();
      case 'big-number':
      default:
        return this._renderAsBigNumbers();
    }
  }

  _renderAsGrid() {
    const {data, options: {gridTitleStyle, gridValueStyle}} = this;

    const rowCount = Math.round(data.length / 2);
    const body = [];

    for (let i = 0; i <= rowCount; i += 2) {
      const left = data[i];
      const right = i < data.length && data[i + 1];

      body.push(
        [
          {text: left.title, style: gridTitleStyle},
          right ? {text: right.title, style: gridTitleStyle} : ''
        ],
        [
          {text: left.value, style: gridValueStyle},
          right ? {text: right.value, style: gridValueStyle} : ''
        ]
      );
    }

    return [{
      table: {
        widths: ['50%', '50%'],
        body,
      },
      colSpan: 2,
      layout: {
        hLineWidth: zero,
        vLineWidth: zero,
        paddingTop: zero,
        paddingLeft: zero,
      }
    }]
  }

  _renderAsRows() {
    const {data} = this;

    return [{
      table: {
        widths: ['*', 'auto'], // maybe [50%, 50%] ?
        body: data.map(({title, value}) => [
          {text: title, style: 'cardRowTitle'},
          {text: value, style: 'cardRowValue'}
        ])
      },
      colSpan: 2,
      layout: {
        hLineColor: () => config.vars.colors.lightSilver,
        hLineWidth: (i, node) => (i > 0 && i !== node.table.body.length)
          ? 1
          : 0,
        vLineWidth: zero,
        paddingLeft: zero,
      }
    }]
  }

  _renderAsBigNumbers() {
    const {data} = this;

    return [{
      table: {
        widths: ['100%', '100%'], // maybe [50%, 50%] ?
        body: data.map(({title, value}) => [
          [{text: title, style: 'cardRowEyebrow'}],
          [{text: value, style: 'cardRowNumber'}]
        ]).flat()
      },
      colSpan: 2,
      layout: {
        hLineWidth: zero,
        vLineWidth: zero,
        paddingLeft: zero,
      }
    }]
  }

  getLayout() {
    return {
      hLineWidth: zero,
      vLineWidth: zero,
      paddingLeft: zero,
      paddingRight: zero
    }
  }

  getRowHeight = (row) => {
    const {options: {iconPosition}} = this;

    const headerRowIdx = 'top' === iconPosition ? 1 : 0;

    if (row === headerRowIdx) {
      return 40;
    }
  }
}

export default ContentCard.asFunction();





